package com.veldekar.components.widgets

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.browser.dom.ElementTarget
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.css.functions.clamp
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment.CenterVertically
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.boxShadow
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxHeight
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.overflow
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.transition
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.overlay.AdvancedPopover
import com.varabyte.kobweb.silk.components.overlay.KeepPopupOpenStrategy
import com.varabyte.kobweb.silk.components.overlay.OpenClosePopupStrategy
import com.varabyte.kobweb.silk.components.overlay.PopupPlacement.BottomRight
import com.varabyte.kobweb.silk.components.overlay.PopupPlacementStrategy
import com.varabyte.kobweb.silk.components.overlay.manual
import com.varabyte.kobweb.silk.components.overlay.never
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.veldekar.model.Bookmark
import com.veldekar.toSitePalette
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.keywords.auto
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.s
import org.jetbrains.compose.web.css.vh

val BookmarkItemStyle = CssStyle {
    base {
        Modifier
            .padding(topBottom = 0.5.cssRem, leftRight = 1.cssRem)
            .transition(Transition.of("background-color", 0.1.s))
    }
    hover {
        Modifier.backgroundColor(colorMode.toSitePalette().nearBackground)
    }
}

@Composable
fun BookmarkMenu(
    icon: String,
    title: String,
    events: List<Bookmark>,
    isOpen: Boolean,
    onToggle: () -> Unit,
    onClose: () -> Unit
) {
    val colorMode = ColorMode.current
    val openCloseStrategy = remember { OpenClosePopupStrategy.manual() }

    LaunchedEffect(isOpen) {
        openCloseStrategy.isOpen = isOpen
    }

    IconButton(
        onClick = onToggle,
        ariaLabel = title
    ) {
        SpanText(icon)
    }

    AdvancedPopover(
        target = ElementTarget.PreviousSibling,
        modifier = Modifier
            .backgroundColor(colorMode.toSitePalette().nearBackground)
            .borderRadius(0.5.cssRem)
            .padding(topBottom = 1.cssRem)
            .boxShadow(offsetX = 0.px, offsetY = 2.px, blurRadius = 8.px, color = Colors.Black.copyf(alpha = 0.15f))
            .minWidth(12.cssRem)
            .maxWidth(clamp(15.cssRem, 90.percent, 25.cssRem))
            .width(auto)
            .maxHeight(70.vh)
            .overflow(Overflow.Auto)
            .zIndex(200),
        openCloseStrategy = openCloseStrategy,
        placementStrategy = PopupPlacementStrategy.of(BottomRight),
        keepOpenStrategy = KeepPopupOpenStrategy.never()
    ) {
        BookmarkList(events, onEventClick = onClose)
    }
}

@Composable
fun BookmarkList(events: List<Bookmark>, onEventClick: (() -> Unit)? = null) {
    Column {
        events.forEach { event ->
            BookmarkItem(event, onEventClick)
        }
    }
}

@Composable
private fun BookmarkItem(event: Bookmark, onEventClick: (() -> Unit)?) {
    Box(
        modifier = BookmarkItemStyle.toModifier()
            .cursor(Cursor.Pointer)
            .onClick {
                // Update hash directly to trigger navigation
                window.location.hash = event.targetAnchor

                // Explicitly scroll into view because content-visibility: auto
                // can confuse the browser's native anchor jump on long pages.
                document.getElementById(event.targetAnchor)?.scrollIntoView()

                // Close the menu
                onEventClick?.invoke()
            }
    ) {
        Row(verticalAlignment = CenterVertically) {
            if (event.icon != null) {
                Box(
                    Modifier
                        .fontSize(2.cssRem)
                        .margin(right = 0.75.cssRem)
                        .styleModifier {
                            property("line-height", "1")
                        }
                ) {
                    SpanText(event.icon)
                }
            }

            Column {
                SpanText(
                    event.title,
                    Modifier.fontWeight(FontWeight.Medium).fontSize(1.1.cssRem)
                )

                val dateText = if (event.isSpan) {
                    "${event.startDate} - ${event.endDate?.day}"
                } else {
                    "${event.startDate}"
                }

                SpanText(
                    dateText,
                    Modifier.fontSize(0.9.cssRem).fontWeight(FontWeight.Normal)
                )
            }
        }
    }
}
